import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crosshair, Search as SearchIcon, Trophy, UserRound } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, Loading, SectionCard, statusTone } from "../../components/ui";
import { useApi } from "../../api/client";
import { usePortalLink } from "../../utils/portalLink";
import { SearchResults } from "../../data/types";

/*
 * One search across the whole portal.
 *
 * Every other page filters within itself, which is no use to a competitor who
 * remembers a name and not the meeting it was shot at. This asks the same
 * question of shooters, events and matches at once, and links straight through.
 */
export default function Search() {
  const link = usePortalLink();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") || "");
  // Debounced, so typing does not fire a request per keystroke.
  const [query, setQuery] = useState(term);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(term);
      setParams(term ? { q: term } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const { data, loading } = useApi<SearchResults | null>(
    query.trim().length >= 2 ? `/search?q=${encodeURIComponent(query.trim())}` : null,
    null
  );

  const shooters = data?.shooters ?? [];
  const competitions = data?.competitions ?? [];
  const matches = data?.matches ?? [];
  const total = shooters.length + competitions.length + matches.length;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-5">
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Search" }]} />

      <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Search</h1>
      <p className="mt-1.5 text-[16px] leading-relaxed text-slate-600 sm:text-[17px]">
        Competitors, events and matches — by name, SID, EID, MID, club or range.
      </p>

      <div className="card mt-4 p-3">
        <div className="relative">
          <SearchIcon size={22} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            autoFocus
            className="input h-14 pl-12 text-[17px]"
            placeholder="Try a name, a SID, a club, or a range…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
      </div>

      {query.trim().length < 2 ? (
        <p className="mt-6 text-center text-[15px] text-muted">Type at least two characters.</p>
      ) : loading ? (
        <div className="card mt-4"><Loading /></div>
      ) : !total ? (
        <div className="card mt-4">
          <EmptyState icon={SearchIcon} title={`Nothing found for “${query.trim()}”`} message="Try part of a name, a SID, or the club code." />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {!!shooters.length && (
            <SectionCard title={`Competitors (${shooters.length})`} bodyClassName="p-0">
              <div className="divide-y divide-line">
                {shooters.map((s) => (
                  <Link
                    key={s.sid}
                    to={link(`/shooters/${s.sid}`)}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50"
                  >
                    <UserRound size={16} className="shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold text-ink">{s.name}</span>
                      <span className="block truncate text-[12px] text-muted">
                        SID {s.sid}
                        {s.club ? ` · ${s.club}` : ""}
                        {s.clubName ? ` ${s.clubName}` : ""}
                      </span>
                    </span>
                    <span className="hidden shrink-0 gap-1 sm:flex">
                      {(s.classes?.length ? s.classes : [s.rifleClass]).filter(Boolean).map((c) => (
                        <span key={c} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-ink">{c}</span>
                      ))}
                    </span>
                  </Link>
                ))}
              </div>
            </SectionCard>
          )}

          {!!competitions.length && (
            <SectionCard title={`Events (${competitions.length})`} bodyClassName="p-0">
              <div className="divide-y divide-line">
                {competitions.map((c) => (
                  <Link
                    key={c.id}
                    to={link(`/competitions/${c.id}/results`)}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50"
                  >
                    <Trophy size={16} className="shrink-0 text-warning" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold text-ink">{c.name}</span>
                      <span className="block truncate text-[12px] text-muted">
                        {[c.eid ? `EID ${c.eid}` : "", c.startDate, c.range, c.stateCode].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                  </Link>
                ))}
              </div>
            </SectionCard>
          )}

          {!!matches.length && (
            <SectionCard title={`Matches (${matches.length})`} bodyClassName="p-0">
              <div className="divide-y divide-line">
                {matches.map((m) => (
                  <Link
                    key={m.id}
                    to={link(`/competitions/${m.competitionId}/events/${m.eventId}/matches/${m.id}`)}
                    className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50"
                  >
                    <Crosshair size={16} className="shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold text-ink">{m.name}</span>
                      <span className="block truncate text-[12px] text-muted">
                        {[m.competitionName, m.distance, m.date, m.mid ? `MID ${m.mid}` : ""].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}
